/**
 * ADIACENT - Advanced Search (bypass Query Service / Elasticsearch)
 *
 * Dialog for looking a material up by exact Codice72 and/or Manufacturer Part
 * Number, for materials that exist in the DB but are not yet searchable through
 * the Query Service / Elasticsearch path.
 *
 * Open state is owned by the header (local useState), the way StyledReasonDialog
 * and the header's own showSearchBar do it - there is nothing global about this
 * dialog, so it needs no redux slice.
 */
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledTextField,
  StyledButton,
  StyledTypography,
} from "@hcl-commerce-store-sdk/react-component";

import { ADVANCED_SEARCH } from "../../../constants/routes";

/** Every Codice72 in the catalog is exactly 8 digits, so anything else is
 *  invalid input rather than a search that finds nothing. Mirrors the backend
 *  rule, which answers 400 - checking here saves the round trip. */
const CODICE72_PATTERN = /^\d{8}$/;

interface AdvancedSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

const AdvancedSearchDialog: React.FC<AdvancedSearchDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [codice72, setCodice72] = useState<string>("");
  const [mfPartNumber, setMfPartNumber] = useState<string>("");
  const [touched, setTouched] = useState<boolean>(false);

  const trimmedCodice72 = codice72.trim();
  const trimmedMfPartNumber = mfPartNumber.trim();

  const codice72Invalid = useMemo(
    () => trimmedCodice72 !== "" && !CODICE72_PATTERN.test(trimmedCodice72),
    [trimmedCodice72]
  );

  // Disabled while both fields are empty; also blocked on an invalid Codice72.
  const canSearch = (trimmedCodice72 !== "" || trimmedMfPartNumber !== "") && !codice72Invalid;

  const clearFields = () => {
    setCodice72("");
    setMfPartNumber("");
    setTouched(false);
  };

  /** Close and discard, for Cancel, the X and the backdrop alike. */
  const close = () => {
    clearFields();
    onClose();
  };

  const submit = (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    setTouched(true);
    if (!canSearch) {
      return;
    }

    // The criteria live in the query string: that is what makes the results
    // page refreshable and linkable without any client state.
    const params = new URLSearchParams();
    if (trimmedCodice72 !== "") {
      params.set("codice72", trimmedCodice72);
    }
    if (trimmedMfPartNumber !== "") {
      params.set("mfPartNumber", trimmedMfPartNumber);
    }

    close();
    history.push(`${ADVANCED_SEARCH}?${params.toString()}`);
  };

  return (
    <StyledDialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby="advanced-search-dialog">
      <StyledDialogTitle
        id="advanced-search-dialog"
        title={t("AdvancedSearch.dialogTitle")}
        onClickHandler={close}
      />
      <StyledDialogContent>
        {/* a form so Enter submits, as SessionErrorDialog does */}
        <form onSubmit={submit} noValidate>
          <StyledTextField
            margin="normal"
            fullWidth
            autoFocus
            autoComplete="off"
            name="codice72"
            label={t("AdvancedSearch.codice72")}
            placeholder={t("AdvancedSearch.codice72Placeholder")}
            value={codice72}
            error={touched && codice72Invalid}
            helperText={
              touched && codice72Invalid
                ? t("AdvancedSearch.codice72Invalid")
                : t("AdvancedSearch.codice72Hint")
            }
            onChange={(e) => setCodice72(e.target.value)}
            onBlur={() => setTouched(true)}
          />

          <StyledTextField
            margin="normal"
            fullWidth
            autoComplete="off"
            name="mfPartNumber"
            label={t("AdvancedSearch.manufacturerPartNumber")}
            placeholder={t("AdvancedSearch.manufacturerPartNumberPlaceholder")}
            helperText={t("AdvancedSearch.manufacturerPartNumberHint")}
            value={mfPartNumber}
            onChange={(e) => setMfPartNumber(e.target.value)}
          />

          <StyledDialogActions>
            <StyledButton size="small" color="secondary" variant="text" onClick={clearFields}>
              <StyledTypography variant="body2">{t("AdvancedSearch.clear")}</StyledTypography>
            </StyledButton>
            <StyledButton size="small" color="secondary" onClick={close}>
              {t("AdvancedSearch.cancel")}
            </StyledButton>
            <StyledButton size="small" color="primary" type="submit" disabled={!canSearch}>
              {t("AdvancedSearch.search")}
            </StyledButton>
          </StyledDialogActions>
        </form>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default AdvancedSearchDialog;
export { AdvancedSearchDialog };
